import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/assets";
import { streamSSE } from "@/components/sse";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const WELCOME = {
  role: "assistant",
  text: "You're talking to Venom, the DGE concierge. Ask me about beat licensing, booking, services or merch.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    sessionRef.current = sessionStorage.getItem("dge-chat-session") || crypto.randomUUID();
    sessionStorage.setItem("dge-chat-session", sessionRef.current);
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      await streamSSE(
        `${API}/assistant/chat`,
        { session_id: sessionRef.current, message: text },
        (delta) => {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, text: last.text + delta };
            return copy;
          });
        }
      );
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: err.message || "Something glitched — try again." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Chat with the label"}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-venom text-obsidian flex items-center justify-center shadow-[0_8px_30px_rgba(57,255,20,0.35)] hover:scale-110 active:scale-95 transition-transform duration-300"
        data-testid="assistant-open-button"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="fixed bottom-[84px] right-4 sm:right-5 z-[60] w-[calc(100vw-2rem)] max-w-sm bg-charcoal border border-crystal/35 rounded-2xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)] flex flex-col"
            style={{ height: "min(480px, calc(100vh - 130px))" }}
            data-testid="assistant-panel"
          >
            <div className="px-5 py-4 border-b border-white/10 bg-obsidian/60">
              <p className="font-display text-sm font-bold text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-venom animate-pulse" /> Venom — Label Concierge
              </p>
              <p className="text-[11px] text-ice/45 mt-0.5">Ask about beats, licensing, booking, merch</p>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-venom text-obsidian rounded-br-md"
                        : "bg-obsidian/80 border border-white/10 text-ice/85 rounded-bl-md"
                    }`}
                  >
                    {m.text || (m.role === "assistant" && sending && i === messages.length - 1 ? (
                      <Loader2 size={14} className="animate-spin text-venom" />
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={send} className="p-3 border-t border-white/10 bg-obsidian/60 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything…"
                maxLength={1000}
                className="flex-1 bg-obsidian border border-white/12 rounded-full px-4 py-2.5 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-venom/70"
                data-testid="assistant-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                aria-label="Send message"
                className="w-10 h-10 shrink-0 rounded-full bg-venom text-obsidian flex items-center justify-center hover:scale-105 active:scale-95 disabled:opacity-50 transition-all"
                data-testid="assistant-send"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
